import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { trackBlogPostCreate, trackAdminBlogCreated } from '../utils/analytics';
import {
    EditIcon as Edit2, SaveIcon as Save,
    RefreshIcon as RefreshCw, UploadIcon, DeleteIcon,
    ArrowLeftIcon as ArrowLeft
} from '../components/Icons';
import { HelpCircle } from 'lucide-react';
import MicroTour from '../components/MicroTour';

// Lazy load ReactQuill
let ReactQuill = null;
try {
    ReactQuill = require('react-quill-new').default;
    require('react-quill-new/dist/quill.snow.css');
} catch (e) {
    console.warn('react-quill-new not available, falling back to textarea');
}

const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote', 'link', 'image'],
        ['clean'],
    ],
};

const CreateBlogPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, API_URL } = useContext(AuthContext);

    const eventIdParam = searchParams.get('eventId');
    const titleParam = searchParams.get('title');

    // Form state
    const [title, setTitle] = useState(titleParam ? decodeURIComponent(titleParam) : '');
    const [content, setContent] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [images, setImages] = useState([]); // [{ url, title, caption }]
    const [linkedEvent, setLinkedEvent] = useState(eventIdParam || '');
    const [linkedGroup, setLinkedGroup] = useState('');
    const [backgroundColor, setBackgroundColor] = useState('#cccccc');
    const [createdPost, setCreatedPost] = useState(null);

    // Data
    const [events, setEvents] = useState([]);
    const [groups, setGroups] = useState([]);

    // UI
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [showMicroTour, setShowMicroTour] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [eventsRes, groupsRes] = await Promise.all([
                    axios.get(`${API_URL}/events`),
                    axios.get(`${API_URL}/groups`)
                ]);
                const data = eventsRes.data;
                setEvents(Array.isArray(data) ? data : []);
                setGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);

                if (eventIdParam && Array.isArray(data)) {
                    const matchedEvent = data.find(e => e._id === eventIdParam || e.slug === eventIdParam);
                    if (matchedEvent) {
                        setLinkedEvent(matchedEvent._id);
                        if (matchedEvent.linkedGroup) {
                            setLinkedGroup(matchedEvent.linkedGroup);
                        }
                        if (!title) {
                            setTitle(`Rückblick: ${matchedEvent.title}`);
                        }
                        if (!imageUrl && (matchedEvent.imageUrl || matchedEvent.pageContent?.hero?.imageUrl)) {
                            const mainImg = matchedEvent.imageUrl || matchedEvent.pageContent?.hero?.imageUrl;
                            setImageUrl(mainImg);
                            setImages([{ url: mainImg, title: matchedEvent.title, caption: '' }]);
                        }
                    }
                }
            } catch (err) {
                console.error('Failed to fetch events or groups', err);
            }
        };
        fetchData();
    }, [API_URL, eventIdParam]);

    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setIsUploading(true);
        setMessage('');

        try {
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${user.token}`,
                },
            };

            const uploadUrl = `${API_URL}/files/upload`;
            const uploadPromises = files.map(async (file) => {
                const formData = new FormData();
                formData.append('file', file);
                const res = await axios.post(uploadUrl, formData, config);
                return {
                    url: res.data.url,
                    title: file.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, " "),
                    caption: ''
                };
            });

            const uploadedItems = await Promise.all(uploadPromises);
            setImages(prev => {
                const updated = [...prev, ...uploadedItems];
                if (!imageUrl && updated.length > 0) {
                    setImageUrl(updated[0].url);
                }
                return updated;
            });
            setMessage(`${files.length} Bild(er) erfolgreich hochgeladen!`);
            setIsError(false);
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Fehler beim Hochladen der Bilder.';
            setMessage(errorMessage);
            setIsError(true);
        } finally {
            setIsUploading(false);
            e.target.value = '';
        }
    };

    const handleUpdateImageMeta = (idx, field, val) => {
        setImages(prev => prev.map((item, i) => i === idx ? { ...item, [field]: val } : item));
    };

    const handleRemoveGalleryImage = (idx) => {
        setImages(prev => {
            const updated = prev.filter((_, i) => i !== idx);
            if (imageUrl === prev[idx]?.url) {
                setImageUrl(updated[0]?.url || '');
            }
            return updated;
        });
    };

    const handleSetPrimaryImage = (idx) => {
        setImages(prev => {
            const selected = prev[idx];
            const rest = prev.filter((_, i) => i !== idx);
            const updated = [selected, ...rest];
            setImageUrl(selected.url);
            return updated;
        });
    };

    const handleSubmit = async (redirectInstagram = false) => {
        if (!title.trim()) {
            setMessage('Bitte gib einen Titel ein.');
            setIsError(true);
            return;
        }
        if (!content.trim()) {
            setMessage('Bitte schreibe Inhalt.');
            setIsError(true);
            return;
        }

        setIsSubmitting(true);
        setMessage('');
        setIsError(false);

        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user.token}`,
                },
            };

            const primaryImg = imageUrl || (images.length > 0 ? images[0].url : null);

            let newPost = {
                title,
                content,
                imageUrl: primaryImg,
                images: images.length > 0 ? images : null,
                linkedEvent: linkedEvent || null,
                linkedGroup: linkedGroup || null
            };

            if (!primaryImg) {
                newPost.backgroundColor = backgroundColor;
            }

            const res = await axios.post(`${API_URL}/blog`, newPost, config);
            trackBlogPostCreate();
            trackAdminBlogCreated(title);

            setCreatedPost(res.data);

            if (redirectInstagram) {
                navigate('/instagram-generator', {
                    state: {
                        title,
                        subtitle: '',
                        images: images.length > 0 ? images : (primaryImg ? [{ url: primaryImg, title, caption: '' }] : [])
                    }
                });
                return;
            }

            const isDraft = res.data.status === 'draft';
            if (isDraft) {
                setMessage('Blogbeitrag erfolgreich eingereicht!');
                setTimeout(() => navigate('/blog'), 2000);
            } else if (res.data?._id) {
                setMessage('Blogbeitrag erfolgreich veröffentlicht!');
                setIsError(false);
                setTimeout(() => navigate(`/edit/blog/${res.data._id}`), 1200);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Fehler beim Erstellen des Blogbeitrags.';
            setMessage(errorMessage);
            setIsError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const canPublish = user?.role === 'admin' || user?.role === 'gruppenleiter';

    return (
        <div className="min-h-screen bg-surface text-surface-on pb-20">
            <MicroTour 
                tourId="create-blog" 
                isOpen={showMicroTour} 
                onClose={() => setShowMicroTour(false)} 
            />

            {/* Edit Mode Banner */}
            <div className="edit-mode-banner px-4 md:px-6 py-3 flex items-center justify-between sticky top-0 z-50 backdrop-blur-md">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/blog')}
                        className="w-8 h-8 rounded-full hover:bg-surface-on/5 flex items-center justify-center transition-colors cursor-pointer"
                    >
                        <ArrowLeft sx={{ fontSize: 20 }} />
                    </button>
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center edit-pen-glow">
                        <Edit2 sx={{ fontSize: 18 }} className="text-primary" />
                    </div>
                    <div>
                        <span className="text-label-large font-bold text-primary">Neuen Blogbeitrag erstellen</span>
                        <span className="text-label-small text-surface-onVariant ml-2 hidden sm:inline">Klicke auf Felder um sie zu bearbeiten</span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setShowMicroTour(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary text-xs font-semibold transition-colors cursor-pointer mr-1"
                        title="Erklärung zum Blog-Editor anzeigen"
                    >
                        <HelpCircle className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Hilfe / Tour</span>
                    </button>
                    {message && (
                        <span className={`text-label-medium font-bold animate-fade-in ${isError ? 'text-error' : 'text-primary'}`}>
                            {isError ? '⚠️' : '✓'} {typeof message === 'object' ? message.text : message}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={() => handleSubmit(true)}
                        disabled={isSubmitting}
                        className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-400 hover:to-pink-400 text-white font-bold text-xs shadow-md transition-all cursor-pointer"
                        title="Speichern und direkt Instagram Karussell generieren"
                    >
                        <span>📸</span>
                        <span className="hidden md:inline">Speichern & Instagram Generator</span>
                    </button>
                    <button
                        data-microtour="blog-submit"
                        onClick={() => handleSubmit(false)}
                        disabled={isSubmitting}
                        className="md3-btn-filled flex items-center gap-2 cursor-pointer"
                    >
                        {isSubmitting ? (
                            <RefreshCw className="animate-spin" sx={{ fontSize: 18 }} />
                        ) : (
                            <Save sx={{ fontSize: 18 }} />
                        )}
                        <span>{canPublish ? 'Veröffentlichen' : 'Einreichen'}</span>
                    </button>
                </div>
            </div>

            {/* Hero — Image upload */}
            <div data-microtour="blog-cover" className="relative h-[40vh] md:h-[50vh] overflow-hidden group">
                {imageUrl ? (
                    <>
                        <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <button
                                onClick={() => setImageUrl('')}
                                className="md3-btn-filled bg-error text-error-on gap-2"
                            >
                                <DeleteIcon sx={{ fontSize: 18 }} />
                                Bild entfernen
                            </button>
                        </div>
                    </>
                ) : (
                    <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-primary/5 transition-colors"
                        style={{ backgroundColor: backgroundColor || 'rgb(var(--color-surface-container-high))' }}
                    >
                        <div className="flex flex-col items-center justify-center">
                            {isUploading ? (
                                <RefreshCw className="animate-spin text-primary mb-3" sx={{ fontSize: 48 }} />
                            ) : (
                                <div className="w-20 h-20 rounded-full bg-surface/80 backdrop-blur flex items-center justify-center text-primary mb-4 group-hover:scale-110 transition-transform shadow-lg">
                                    <UploadIcon sx={{ fontSize: 36 }} />
                                </div>
                            )}
                            <p className="text-title-medium text-surface-on font-bold bg-surface/60 backdrop-blur px-4 py-1 rounded-full">
                                {isUploading ? 'Wird hochgeladen...' : 'Titelbild hochladen'}
                            </p>
                            <p className="text-body-small text-surface-onVariant mt-2 bg-surface/40 backdrop-blur px-3 py-0.5 rounded-full">
                                JPG, PNG oder WEBP (Max. 10MB)
                            </p>
                        </div>
                        <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={handleFileUpload}
                            disabled={isUploading}
                        />
                    </label>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/40 to-transparent pointer-events-none" />
            </div>

            <div className="container -mt-24 relative z-10">
                <div className="flex flex-col lg:flex-row gap-8 max-w-7xl mx-auto">
                    {/* Main Content */}
                    <article className="flex-1 md3-card-elevated bg-surface-containerLow p-6 md:p-12 shadow-elevation-3">
                        {/* Title */}
                        <div data-microtour="blog-title" className="mb-8 p-3 rounded-2xl bg-surface-containerLowest/50 border border-outlineVariant/20">
                            <label className="block text-xs font-bold text-surface-onVariant uppercase tracking-wider mb-1">Titel des Beitrags</label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full text-display-small md:text-display-medium font-bold text-surface-on bg-transparent border-none outline-none placeholder:text-surface-onVariant/30"
                                placeholder="Titel eingeben..."
                            />
                        </div>

                        {/* Content Editor */}
                        <div data-microtour="blog-editor" className="mb-8">
                            {ReactQuill ? (
                                <ReactQuill
                                    theme="snow"
                                    value={content}
                                    onChange={setContent}
                                    modules={quillModules}
                                    placeholder="Schreibe hier deinen Beitrag..."
                                />
                            ) : (
                                <textarea
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    className="w-full min-h-[400px] p-4 bg-surface-containerLowest border border-outlineVariant rounded-xl text-surface-on text-body-large leading-relaxed"
                                    placeholder="Schreibe hier deinen Beitrag..."
                                />
                            )}
                        </div>

                        {/* Bilder-Galerie & Instagram-Metadaten */}
                        <div className="mt-8 pt-8 border-t border-outlineVariant/30">
                            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                                <div>
                                    <h3 className="text-title-medium font-bold text-surface-on flex items-center gap-2">
                                        <span>📸 Bilder, Bildtitel & Instagram-Vorbereitung</span>
                                    </h3>
                                    <p className="text-body-small text-surface-onVariant mt-0.5">
                                        Gib jedem Bild einen Titel und eine kurze Beschreibung für die Website-Galerie und den automatischen Instagram-Generator.
                                    </p>
                                </div>
                                <label className="md3-btn-tonal flex items-center gap-2 cursor-pointer text-xs font-bold py-2 px-4 rounded-xl">
                                    <UploadIcon sx={{ fontSize: 18 }} />
                                    <span>{isUploading ? 'Lädt...' : 'Fotos hinzufügen'}</span>
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={handleFileUpload}
                                        disabled={isUploading}
                                        className="hidden"
                                    />
                                </label>
                            </div>

                            {images.length === 0 ? (
                                <div className="p-6 rounded-2xl border-2 border-dashed border-outlineVariant/50 bg-surface-containerLowest/50 text-center">
                                    <p className="text-body-medium text-surface-onVariant">Noch keine Galeriebilder hochgeladen.</p>
                                    <p className="text-body-small text-surface-onVariant/70 mt-1">Lade oben ein Titelbild oder hier mehrere Fotos für das Karussell hoch.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {images.map((img, idx) => (
                                        <div key={idx} className="p-3.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/30 flex gap-3 relative group">
                                            <div className="w-24 h-24 rounded-xl overflow-hidden bg-surface-containerHigh shrink-0 relative">
                                                <img src={img.url} alt="" className="w-full h-full object-cover" />
                                                {idx === 0 && (
                                                    <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-primary text-primary-on text-[10px] font-bold">
                                                        Titelbild
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0 space-y-1.5">
                                                <input
                                                    type="text"
                                                    value={img.title || ''}
                                                    onChange={(e) => handleUpdateImageMeta(idx, 'title', e.target.value)}
                                                    placeholder="Bildtitel (z. B. Baden im Freibad)"
                                                    className="w-full text-xs font-bold bg-surface-container border border-outlineVariant/30 rounded-lg px-2.5 py-1.5 text-surface-on focus:outline-none focus:border-primary"
                                                />
                                                <textarea
                                                    rows={2}
                                                    value={img.caption || ''}
                                                    onChange={(e) => handleUpdateImageMeta(idx, 'caption', e.target.value)}
                                                    placeholder="Kurzbeschreibung (z. B. Nach unserer langen Wanderung...)"
                                                    className="w-full text-[11px] bg-surface-container border border-outlineVariant/30 rounded-lg px-2.5 py-1 text-surface-onVariant focus:outline-none focus:border-primary resize-none"
                                                />
                                                <div className="flex items-center gap-2 pt-0.5">
                                                    {idx !== 0 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleSetPrimaryImage(idx)}
                                                            className="text-[10px] font-semibold text-primary hover:underline cursor-pointer"
                                                        >
                                                            Als Titelbild
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveGalleryImage(idx)}
                                                        className="text-[10px] font-semibold text-error hover:underline ml-auto cursor-pointer"
                                                    >
                                                        Löschen
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </article>

                    {/* Sidebar */}
                    <aside className="lg:w-80 flex-shrink-0 space-y-6">
                        <div className="md3-card-outlined p-6 sticky top-20">
                            <h3 className="text-title-medium font-bold mb-4 flex items-center gap-2">
                                <Edit2 sx={{ fontSize: 18 }} className="text-primary" />
                                Einstellungen
                            </h3>

                            {/* Linked Event */}
                            <div className="space-y-2 mb-6">
                                <label className="text-label-medium text-surface-onVariant font-bold">Verknüpftes Event</label>
                                <select
                                    value={linkedEvent}
                                    onChange={(e) => setLinkedEvent(e.target.value)}
                                    className="md3-textfield-outlined h-12 text-body-medium"
                                >
                                    <option value="">Kein Event verknüpft</option>
                                    {events.map((event) => (
                                        <option key={event._id} value={event._id}>
                                            {event.title} ({new Date(event.date).toLocaleDateString()})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Linked Group / Stufe */}
                            <div className="space-y-2 mb-6">
                                <label className="text-label-medium text-surface-onVariant font-bold">Verknüpfte Gruppe / Stufe</label>
                                <select
                                    value={linkedGroup}
                                    onChange={(e) => setLinkedGroup(e.target.value)}
                                    className="md3-textfield-outlined h-12 text-body-medium"
                                >
                                    <option value="">Keine Gruppe verknüpft</option>
                                    {groups.map((group) => (
                                        <option key={group._id} value={group.slug || group._id}>
                                            {group.name} ({group.altersstufe || 'Gruppe'})
                                        </option>
                                    ))}
                                </select>
                                <p className="text-[11px] text-surface-onVariant">
                                    Berichte werden automatisch auf der Gruppen-Detailseite angezeigt.
                                </p>
                            </div>

                            {/* Background Color */}
                            {!imageUrl && (
                                <div className="space-y-2 mb-6">
                                    <label className="text-label-medium text-surface-onVariant font-bold">Hintergrundfarbe</label>
                                    <p className="text-body-small text-surface-onVariant">Wird angezeigt wenn kein Bild vorhanden ist</p>
                                    <div className="flex items-center gap-3 mt-2">
                                        <div className="w-10 h-10 rounded-xl shadow-inner border border-outlineVariant" style={{ backgroundColor }}></div>
                                        <input
                                            type="color"
                                            value={backgroundColor}
                                            onChange={(e) => setBackgroundColor(e.target.value)}
                                            className="w-10 h-10 rounded-lg cursor-pointer border-none overflow-hidden"
                                        />
                                        <span className="font-mono text-label-medium font-bold text-primary">{backgroundColor.toUpperCase()}</span>
                                    </div>
                                </div>
                            )}

                            {/* Image URL Fallback */}
                            <div className="space-y-2 mb-6">
                                <label className="text-label-medium text-surface-onVariant font-bold">Oder Bild-URL verwenden</label>
                                <input
                                    type="url"
                                    value={imageUrl}
                                    onChange={(e) => setImageUrl(e.target.value)}
                                    className="md3-textfield-outlined text-body-medium"
                                    placeholder="https://..."
                                />
                            </div>

                            <div className="pt-4 border-t border-outlineVariant">
                                <p className="text-body-small text-surface-onVariant italic">
                                    {canPublish
                                        ? 'Als Admin/Gruppenleiter wird dein Beitrag direkt veröffentlicht.'
                                        : 'Dein Beitrag wird als Entwurf gespeichert und muss von einem Gruppenleiter freigegeben werden.'}
                                </p>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default CreateBlogPage;
